import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Download, CalendarDays, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "conges_payes", l: "Congés payés" },
  { v: "rtt", l: "RTT" },
  { v: "maladie", l: "Congé maladie" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));

export default function EmployeeLeaves() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {nb_jours, balance, reserved, available, ok}
  const [form, setForm] = useState({ type_conge: "conges_payes", date_debut: "", date_fin: "", motif: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/leaves/my"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live preview whenever type or dates change
  useEffect(() => {
    if (!open || !form.date_debut || !form.date_fin || !form.type_conge) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/leaves/preview", { params: form });
        setPreview(data);
      } catch (e) { setPreview({ error: formatApiError(e) }); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, form.date_debut, form.date_fin, form.type_conge, form]);

  const submit = async (e) => {
    e.preventDefault();
    if (preview && preview.ok === false) {
      toast.error(`Solde insuffisant : il reste ${Math.max(0, preview.available)} jour(s) disponibles pour ${TYPE_LABEL[form.type_conge]}.`);
      return;
    }
    setLoading(true);
    try {
      await api.post("/leaves", form);
      toast.success("Demande envoyée à la RH");
      setOpen(false);
      setForm({ type_conge: "conges_payes", date_debut: "", date_fin: "", motif: "" });
      setPreview(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const dl = async (id) => {
    try { await downloadPdf(`/leaves/${id}/pdf`, `lettre_conge_${id.slice(0,8)}.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Mes congés"
        subtitle="Suivez vos demandes et téléchargez vos lettres de congé approuvées."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-leave-button" className="bg-[#0066FF] hover:bg-[#0052CC]"><Plus className="h-4 w-4 mr-2" />Nouvelle demande</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nouvelle demande de congé</DialogTitle>
                <DialogDescription>Choisissez le type de congé, les dates et le motif. Le solde disponible est calculé en direct.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="leave-form">
                <div>
                  <Label>Type de congé</Label>
                  <Select value={form.type_conge} onValueChange={(v) => setForm({ ...form, type_conge: v })}>
                    <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date de début</Label>
                    <Input data-testid="leave-start-input" type="date" required value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input data-testid="leave-end-input" type="date" required value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} />
                  </div>
                </div>

                {preview && !preview.error && (
                  <div
                    data-testid="leave-preview-box"
                    className={`rounded-md border p-3 text-sm ${preview.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`}
                  >
                    <div className="flex items-start gap-2">
                      {preview.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <AlertTriangle className="h-4 w-4 mt-0.5" />}
                      <div>
                        <div className="font-medium">
                          {preview.nb_jours} jour(s) ouvré(s) — dimanches & jours fériés exclus
                        </div>
                        <div className="text-xs mt-1 opacity-90">
                          Solde {TYPE_LABEL[form.type_conge]} : <b>{preview.balance}</b> j
                          {preview.reserved > 0 && <> · réservés/pris : <b>{preview.reserved}</b> j</>}
                          {" "}· disponibles : <b>{Math.max(0, preview.available)}</b> j
                        </div>
                        {!preview.ok && preview.nb_jours > 0 && (
                          <div className="text-xs mt-1 font-medium">Solde insuffisant — impossible de soumettre.</div>
                        )}
                        {preview.nb_jours === 0 && (
                          <div className="text-xs mt-1 font-medium">Aucun jour ouvré sur cette période.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <Label>Motif</Label>
                  <Textarea data-testid="leave-motif-input" required rows={3} value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} placeholder="Décrivez le motif de votre demande..." />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-leave-button" type="submit" disabled={loading || (preview && !preview.ok)} className="bg-[#0066FF] hover:bg-[#0052CC]">
                    {loading ? "Envoi…" : "Soumettre"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDays className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Aucune demande pour le moment</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Jours ouvrés</TableHead>
                <TableHead>Motif</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  <TableCell className="font-medium">{TYPE_LABEL[l.type_conge] || l.type_conge}</TableCell>
                  <TableCell className="text-sm text-slate-600">{l.date_debut} → {l.date_fin}</TableCell>
                  <TableCell>{l.nb_jours}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-slate-600">{l.motif}</TableCell>
                  <TableCell><StatusBadge status={l.status} /></TableCell>
                  <TableCell className="text-right">
                    {l.status === "approuve" ? (
                      <Button data-testid={`download-leave-${l.id}`} size="sm" variant="outline" onClick={() => dl(l.id)}><Download className="h-3.5 w-3.5 mr-1" />Lettre PDF</Button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((l) => (
              <div key={l.id} className="p-4" data-testid={`leave-card-${l.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{TYPE_LABEL[l.type_conge] || l.type_conge}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{l.date_debut} → {l.date_fin} · <b>{l.nb_jours}</b> j ouvrés</div>
                  </div>
                  <StatusBadge status={l.status} />
                </div>
                {l.motif && <p className="text-xs text-slate-600 line-clamp-2 mb-3">{l.motif}</p>}
                {l.status === "approuve" && (
                  <Button data-testid={`download-leave-m-${l.id}`} size="sm" variant="outline" onClick={() => dl(l.id)} className="w-full">
                    <Download className="h-3.5 w-3.5 mr-1" />Télécharger la lettre PDF
                  </Button>
                )}
              </div>
            ))}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
